<template>
  <!-- Step 3, the last one. Mirrors the mobile Username screen (iOS
       OnBoardingUsernameViewController / Android OnboardingUserNameFragment).

       Mobile generates its suggestions by calling OpenAI from the client. That
       doesn't port to the browser — a key shipped to the page is a public key —
       so web composes them locally from the same hiking word list. Same shapes,
       no network, no key. They're labelled plainly as suggestions rather than
       borrowing mobile's "HAILEY" AI branding, since no AI is involved. -->
  <div>
    <OnBoardingStepIndicator :current="5" />

    <div class="back-link mb-3" @click="backTapped">
      <span class="material-icons">arrow_left</span>
      <span>{{ $t('on_boarding.profile_steps.back') }}</span>
    </div>

    <h2 class="auth-heading">{{ $t('on_boarding.profile_username_page.title') }}</h2>
    <p class="auth-sub">{{ $t('on_boarding.profile_username_page.subtitle') }}</p>

    <div v-if="errorMessage" class="alert alert-danger" role="alert">
      {{ errorMessage }}
    </div>

    <form @submit.prevent="submit">
      <div class="pro-field mb-3">
        <label class="pro-field__label" for="profileUsername">
          {{ $t('on_boarding.profile_username_page.placeholder') }}
        </label>
        <input
          id="profileUsername"
          ref="usernameInput"
          :value="username"
          type="text"
          class="form-control form-control-lg pro-field__input"
          :placeholder="$t('on_boarding.profile_username_page.placeholder')"
          autocomplete="nickname"
          :maxlength="maxLength"
          required
          @input="onInput"
        >
        <small v-if="inlineError" class="pro-field__hint pro-field__hint--error">{{ inlineError }}</small>
      </div>

      <div v-if="suggestions.length > 0" class="suggestions mb-4">
        <div class="suggestions__label">
          {{ $t('on_boarding.profile_username_page.suggestions_label') }}
        </div>
        <div class="suggestions__chips">
          <button
            v-for="suggestion in suggestions"
            :key="suggestion"
            type="button"
            class="suggestion-chip"
            :class="{ 'suggestion-chip--active': suggestion === username }"
            @click="pick(suggestion)"
          >
            {{ suggestion }}
          </button>
          <button type="button" class="suggestion-chip suggestion-chip--more" @click="regenerate">
            <span class="material-icons">refresh</span>
            <span>{{ $t('on_boarding.profile_username_page.regenerate') }}</span>
          </button>
        </div>
      </div>

      <button type="submit" class="btn pro-btn pro-btn--accent w-100" :disabled="isLoading || !canSubmit">
        <template v-if="isLoading">
          <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
        </template>
        <template v-else>
          <span class="material-icons">arrow_forward</span>
        </template>
        <span>{{ $t('on_boarding.profile_username_page.continue') }}</span>
      </button>
    </form>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import OnBoardingStepIndicator from './OnBoardingStepIndicator.vue';
import {
  normalizeUsername,
  validateUsername,
  suggestUsernames,
  composeFullName,
  USERNAME_MAX_LENGTH,
  USERNAME_MIN_LENGTH,
} from '../../../utils/helpers/signupProfile';

export default defineComponent({
  name: 'OnBoardingProfileUsernamePage',
  components: { OnBoardingStepIndicator },
  data() {
    return {
      username: this.$store.state.onBoarding.profileUsername || '',
      maxLength: USERNAME_MAX_LENGTH,
      // Only surfaced once the user has actually typed, so the step doesn't
      // open already scolding them.
      touched: false,
    };
  },
  computed: {
    isLoading(): boolean {
      return this.$store.state.onBoarding.isLoading;
    },
    errorMessage(): string | null {
      return this.$store.state.onBoarding.errorMessage;
    },
    suggestions(): string[] {
      return this.$store.state.onBoarding.profileUsernameSuggestions;
    },
    validationError(): string | null {
      const code = validateUsername(this.username);
      if (code === null) return null;
      if (code === 'too_short') {
        return this.$t('on_boarding.profile_username_page.error_too_short', { min: USERNAME_MIN_LENGTH });
      }
      return this.$t('on_boarding.profile_username_page.error_blank');
    },
    inlineError(): string | null {
      // Shown once the user has typed — or straight away when the value we
      // prefilled is itself invalid, since Continue is disabled and silence
      // would leave no clue why.
      if (this.touched) return this.validationError;
      return this.username ? this.validationError : null;
    },
    canSubmit(): boolean {
      return this.validationError === null;
    },
  },
  mounted() {
    (this.$refs.usernameInput as HTMLInputElement | undefined)?.focus();
  },
  methods: {
    // Normalising on input means the field can never hold a value the
    // validator would then reject (spaces become dashes, "@" is dropped).
    //
    // The input is manually controlled (`:value` + `@input`), so when
    // normalisation strips the character just typed the model doesn't change,
    // Vue has no reason to re-render, and the DOM would keep showing text that
    // will never be saved — type "joey@" and the field reads "joey@" while the
    // stored value is "joey". Writing the normalised value back to the element
    // keeps what is displayed and what is saved in step.
    onInput(event: Event) {
      const element = event.target as HTMLInputElement;
      this.touched = true;
      const normalized = normalizeUsername(element.value);
      this.username = normalized;
      if (element.value !== normalized) element.value = normalized;
    },
    pick(suggestion: string) {
      this.username = suggestion;
      this.touched = true;
      this.$store.commit('onBoarding/setError', null);
    },
    regenerate() {
      const state = this.$store.state.onBoarding;
      const fullName = composeFullName(state.profileFirstName, state.profileLastName);
      const fresh = suggestUsernames(fullName, state.user?.email);
      this.$store.commit('onBoarding/setProfileUsernameSuggestions', fresh);
    },
    submit() {
      this.touched = true;
      if (!this.canSubmit) {
        this.$store.commit('onBoarding/setError', this.validationError);
        return;
      }
      this.$store.dispatch('onBoarding/submitProfileUsername', this.username);
    },
    backTapped() {
      this.$store.commit('onBoarding/setError', null);
      this.$store.commit('onBoarding/popPage');
    },
  },
  beforeUnmount() {
    this.$store.commit('onBoarding/setError', null);
  },
});
</script>

<style scoped>
.auth-heading {
  font-family: 'Montserrat', Helvetica, Arial, sans-serif;
  font-size: clamp(1.4rem, 2.4vw, 1.75rem);
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: -0.01em;
  color: #fff;
  margin: 0 0 0.35rem;
}
.auth-sub {
  color: var(--pro-text-muted);
  font-size: 0.95rem;
  margin: 0 0 1.5rem;
}
.back-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--pro-text-muted);
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 600;
}
.back-link:hover {
  color: #fff;
}

.pro-field__hint--error {
  color: #f3737c; /* DS --color-red #DB2B35, lightened for text on dark */
}

.suggestions__label {
  color: var(--pro-text-muted);
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  margin-bottom: 0.5rem;
}
.suggestions__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.suggestion-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0.4rem 0.75rem;
  border-radius: 999px;
  border: 1px solid var(--pro-border-strong);
  background: rgba(255, 255, 255, 0.04);
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}
.suggestion-chip:hover {
  background: rgba(255, 255, 255, 0.09);
  color: #fff;
}
.suggestion-chip--active {
  border-color: var(--yellow, #fddf00);
  background: rgba(253, 223, 0, 0.14);
  color: #fff;
}
.suggestion-chip--more {
  color: var(--pro-text-muted);
}
.suggestion-chip .material-icons {
  font-size: 16px;
}
</style>
